import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup
} from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { MultiHedge, SpotStreamingResponse } from '@approot/models';
import {
  ProductsConfigService,
  RfqDetails,
  RfqService,
  VolBasedRfqOptionPriceV2Result
} from '@approot/services';
import {
  SubscriptionSink,
  Utilities,
  clone,
  exponentialDelay,
  handleChoicePrice,
  shortUuid,
  sleep
} from '@approot/utilities';
import { EMPTY, Subscription, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ProviderQuoteEvent } from '../provider-quote.model';

import dayjs from 'dayjs';
import { filter } from 'rxjs/operators';
import { ProviderMultiHedgeGroupComponent } from '../provider-multi-hedge-group/provider-multi-hedge-group.component';
import { ProviderMultiHedge } from '../provider-quote.model';
import { TabItem } from '../provider-quote.service';
type Spot = 'spotAsk' | 'spotBid';
const whiteColor = 'var(--pure-white)';
const orangeColor = 'var(--orange-text-color)';
const { cleanFormattedNumeric } = Utilities;
@Component({
  selector: 'app-provider-quote-setup',
  templateUrl: './provider-quote-setup.component.html',
  styleUrls: ['./provider-quote-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProviderQuoteSetupComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() public tabItem: TabItem;
  @Input() public cross: string;
  @Input() public quoteEvent: ProviderQuoteEvent;
  @Input() public isPremiumBasedVanillasWithSpotFeed = false;
  @Input() public form: FormGroup;
  @Input() public rfqDetails: RfqDetails;
  @Input() public volBasedRfqOptionPriceResult: VolBasedRfqOptionPriceV2Result;
  @Input({ required: true }) public showExpiryDate: boolean;
  @Input() streamedHedge: ProviderMultiHedge[];
  @Output() public changeInSetupForm = new EventEmitter<void>();
  @ViewChild(ProviderMultiHedgeGroupComponent)
  public hedgeGroupComponent: ProviderMultiHedgeGroupComponent;
  public hedge: string;
  public hedgeRate: number;
  public broker: string;
  public account: string;
  public displayedAccount: string;
  public premiumValue: string;
  public premiumCCY: string;
  public requestId: number;
  public premiumDate: string;
  public show = true;
  public spotInfo = {
    ask: {
      previousInput: null,
      latestStreamedValue: null,
      streamingSpot: true,
      color: whiteColor,
      flashOnChange: true,
      showSpinner: false
    },
    bid: {
      previousInput: null,
      latestStreamedValue: null,
      streamingSpot: true,
      color: whiteColor,
      flashOnChange: true,
      showSpinner: false
    }
  };
  public rfqComment: string;
  private streamingWebsocketUUID = 'stream_' + shortUuid();
  private firstQuote = true;
  private wsSubscription: Subscription;
  private spotStreamingSub: Subscription;
  private subsink = new SubscriptionSink();
  private isElementFocused = false;
  private handleRfqDetailsInit = true;

  public constructor(
    private productsConfigService: ProductsConfigService,
    private websocketService: GlobalWebsocketService,
    private changeRef: ChangeDetectorRef,
    private rfqService: RfqService
  ) {
    effect(() => {
      this.displayedAccount = this.rfqService.displayedAccount();
    });
  }

  public ngOnInit(): void {
    this.formListeners();
    this.handleSpot();
    this.handleRfqDetails();
  }

  public ngOnDestroy(): void {
    this.unsubscribeFromSpot();
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.quoteEvent?.currentValue) {
      this.updateQuote(changes.quoteEvent.currentValue as ProviderQuoteEvent);
    }

    if (changes.rfqDetails?.currentValue) {
      this.handleRfqDetails();
    }

    if (
      changes.volBasedRfqOptionPriceResult?.currentValue &&
      !this.tabItem.isVolBased
    ) {
      if (!changes.volBasedRfqOptionPriceResult?.previousValue) {
        this.createHedges(this.volBasedRfqOptionPriceResult.hedges);
      } else {
        this.updateHedges(clone(this.volBasedRfqOptionPriceResult.hedges));
      }
    }

    if (changes.streamedHedge?.currentValue && this.tabItem.isPremiumBased) {
      if (!(this.form?.get('hedges') as FormArray)?.controls.length) {
        this.createHedges([changes.streamedHedge.currentValue]);
      }
    }
  }

  public spotFieldBlurred(side: 'bid' | 'ask'): void {
    this.onElemBlur();
    const fieldName = side === 'ask' ? 'spotAsk' : 'spotBid';
    const spotCtrl = this.form.get('spots').get(fieldName);
    const hedgesForm = this.form.get('hedges') as FormArray;
    const strike = hedgesForm.controls[0]?.get('strike').value;
    if (spotCtrl.value !== this.spotInfo[side].previousInput) {
      if (spotCtrl.value === '') {
        this.spotInfo[side].streamingSpot = true;
        this.spotInfo[side].flashOnChange = true;
        this.spotInfo[side].color = whiteColor;
        spotCtrl.setValue(this.spotInfo[side].latestStreamedValue);
      }
      this.roundSpot(spotCtrl.value, fieldName);
      this.changeInSpot();
      this.spotInfo[side].previousInput = spotCtrl.value;
    } else if (this.hedge !== 'None' && !Utilities.isNotBlank(strike)) {
      this.changeInSpot();
    }
  }

  public spotFieldEdited(side: 'bid' | 'ask'): void {
    this.spotInfo[side].streamingSpot = false;
    this.spotInfo[side].color = orangeColor;
    this.spotInfo[side].flashOnChange = false;
  }

  public unsubscribeFromSpot(): void {
    if (this.isPremiumBasedVanillasWithSpotFeed) {
      this.websocketService.sendMessage({
        action: `${API_PREFIX_V2}unsubscribe`,
        uuid: this.streamingWebsocketUUID,
        payload: { channels: [`${this.cross} Spot`] }
      });
      this.spotStreamingSub.unsubscribe();
    }
  }

  public changeInForm(): void {
    if (
      this.isElementFocused ||
      (this.hedge !== 'None' &&
        this.hedgeGroupComponent.multiHedgeComponent.find(
          x => x.isElementFocused
        ))
    ) {
      return;
    }
    this.changeInSetupForm.emit();
  }

  public onElemFocus(): void {
    this.isElementFocused = true;
  }

  public onElemBlur(): void {
    this.isElementFocused = false;
  }

  private updateHedges(hedges?: ProviderMultiHedge[]): void {
    const hedgesForm = this.form.get('hedges') as FormArray;
    this.formatHedges(hedges);
    hedges.forEach((hedge: ProviderMultiHedge, hedgeIndex: number) => {
      const hedgeForm = hedgesForm.controls[hedgeIndex];
      Object.keys(hedge).forEach(key => {
        const ctrl = hedgeForm.get(key);
        if (ctrl) {
          ctrl.setValue(hedge[key], { emitEvent: key !== 'settlementDate' });
        } else {
          console.warn(`Control ${key} not found in hedges form`);
        }
      });
    });
  }

  private changeInSpot(): void {
    const hedge = (this.form.get('hedges') as FormArray).controls[0];
    if (
      !this.tabItem.isVolBased &&
      this.volBasedRfqOptionPriceResult &&
      this.hedge !== 'None' &&
      (hedge?.get('strike').dirty || this.quoteEvent.request?.hedgeRate)
    ) {
      return;
    }
    this.changeInForm();
  }

  private updateQuote(quoteEvent: ProviderQuoteEvent) {
    const request = quoteEvent.quote.request;
    if (this.requestId !== request.requestId && this.tabItem.isVolBased) {
      const resubscribe = (iteration: number) => {
        this.wsSubscription = this.websocketService
          .getListener({ action: 'clientPrice' })
          .pipe(filter(r => r.channel === `clientPrice-${request.requestId}`))
          .subscribe({
            next: r => {
              this.handleRfqDetailsInit = false;
              const p: any = r.payload;
              this.rfqComment = p.comment;
              this.premiumCCY = p.premiumCurrency;
              this.premiumDate = dayjs.utc(p.premiumDate).format('D MMM YYYY');
              this.premiumValue = p.premiumValue;
              this.hedge = p.hedgeMode;
              this.changeRef.markForCheck();
              this.createHedges(p.hedges);
            },
            error: async err => {
              console.error(err);
              await sleep(exponentialDelay(iteration));
              resubscribe(iteration + 1);
            },
            complete: async () => {
              await sleep(exponentialDelay(iteration));
              resubscribe(iteration + 1);
            }
          });
      };
      resubscribe(0);
    }
    this.requestId = request.requestId;
    this.broker = request.primeBrokerName;
    this.account = request.account;
    this.hedgeRate = request.hedgeRate;
    if (this.tabItem.isVolBased && !this.firstQuote) {
      this.firstQuote = false;
      return;
    }

    if (this.hedgeRate == null) {
      this.hedge = request.hedge;
    } else {
      const hedge = request.hedge.replace(/^Delta/i, '');
      this.hedge = `Custom ${hedge}`;
    }
    this.premiumValue = quoteEvent.quote.premiumDelivery;
    this.premiumCCY = quoteEvent.quote.premiumCurrency;
    this.premiumDate = dayjs
      .utc(quoteEvent.quote.premiumDate)
      .format('D MMM YYYY');

    this.firstQuote = false;
  }

  private createHedges(hedges: ProviderMultiHedge[]): void {
    const hedgesCtrl = this.form.get('hedges') as FormArray;
    const clonedHedges = clone(hedges);
    this.formatHedges(clonedHedges);
    if (hedgesCtrl.controls.length) {
      this.updateHedges(clonedHedges);
      return;
    }

    const legs = this.tabItem.legs;
    let expiryDate = '';
    try {
      expiryDate = legs[0].leg.leg.preSetPosition.expiryDate || '';
    } catch {}

    const hedgesGroup = clonedHedges.map(x => {
      return new FormGroup({
        productType: new FormControl(x.productType),
        cross: new FormControl(x.cross),
        position: new FormControl(x.position),
        bidPosition: new FormControl(x.bidPosition),
        notional: new FormControl(x.notional),
        strike: new FormControl(x.strike, this.strikeValidator),
        faceCurrency: new FormControl(x.faceCurrency),
        settlementDate: new FormControl(x.settlementDate),
        expiryDate: new FormControl(expiryDate),
        impliedSpot: new FormControl(x.impliedSpot)
      });
    });

    hedgesGroup.forEach(x => {
      hedgesCtrl.push(x);
    });
  }

  private formatHedges(hedges: ProviderMultiHedge[]): void {
    hedges.forEach(x => {
      x.settlementDate = dayjs.utc(x.settlementDate).format('D MMM YYYY');
      x.bidPosition = x.position === 'Buy' ? 'Sell' : 'Buy';
      x.strike = this.productsConfigService.crossDecimalFormatter(
        x.strike,
        this.cross
      );
    });
  }

  private handleSpot(): void {
    if (this.isPremiumBasedVanillasWithSpotFeed) {
      this.initSpotListener();
      this.subToSpot();
    }
  }

  private subToSpot(): void {
    this.websocketService.sendMessage({
      action: `${API_PREFIX_V2}subscribe`,
      uuid: this.streamingWebsocketUUID,
      payload: { channels: [`${this.cross} Spot`] }
    });
  }

  private initSpotListener(): void {
    const decimalPlacesFormat =
      this.productsConfigService.decimalPlacesForCross(this.cross);
    const spotsGroup = this.form.get('spots');
    this.spotInfo.ask.showSpinner = true;
    this.spotInfo.bid.showSpinner = true;
    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        console.log('There was a problem getting the spot');
        return;
      }
      this.spotStreamingSub = this.websocketService
        .getListener<SpotStreamingResponse>({
          action: 'marketData',
          uuid: this.streamingWebsocketUUID,
          take: Infinity
        })
        .pipe(
          catchError(err => {
            console.error('initSpotListener', err);
            return EMPTY;
          }),
          switchMap(r => {
            if (!r || r.error) {
              return EMPTY;
            }
            return of(r);
          })
        )
        .subscribe({
          next: x => {
            this.spotInfo.ask.showSpinner = false;
            this.spotInfo.bid.showSpinner = false;
            let changeInSpot = false;
            ['ask', 'bid'].forEach(side => {
              const spotSide = side === 'ask' ? 'spotAsk' : 'spotBid';
              const spotCtrl = spotsGroup.get(spotSide);
              if (this.spotInfo[side].streamingSpot) {
                const val = x.payload[side].toFixed(decimalPlacesFormat);
                if (spotCtrl.value !== val) {
                  changeInSpot = true;
                  this.spotInfo[side].latestStreamedValue = val;
                  this.roundSpot(val, spotSide);
                }
              }
            });
            if (changeInSpot) {
              this.changeInSpot();
            }
          },
          complete: async () => {
            await sleep(500);
            resubscribe(iteration + 1);
          },
          error: async err => {
            console.error(err);
            await sleep(500);
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleSpotChoicePrice(fieldName: Spot, value: string): void {
    const spotsGroup = this.form.get('spots') as FormGroup;
    let newValue = this.productsConfigService.crossDecimalFormatter(
      Utilities.formattedNumberToNumber(value),
      this.cross
    );
    if (
      handleChoicePrice(
        spotsGroup,
        fieldName,
        !isNaN(newValue) ? newValue.toString() : ''
      )
    ) {
      this.spotFieldEdited(fieldName === 'spotAsk' ? 'bid' : 'ask');
    }
  }

  private formListeners(): void {
    ['spotAsk', 'spotBid'].forEach((spotField: Spot) => {
      this.subsink.sink = this.form
        .get('spots')
        ?.get(spotField)
        .valueChanges.subscribe(val => {
          if (val.includes('ch')) {
            this.handleSpotChoicePrice(spotField, val);
          }
        });
    });
  }

  private roundSpot(val: string, field: 'spotAsk' | 'spotBid'): void {
    const spotsGroup = this.form.get('spots');
    const roundedValue = this.productsConfigService.crossDecimalFormatter(
      val,
      this.cross
    );
    spotsGroup.get(field).setValue(roundedValue);
  }

  private strikeValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    const value = control.value;
    if (value == null || value === '' || value > 0) {
      return null;
    }
    return { invalidEntry: { value: value } };
  }

  private rfqHedgeToProviderHedge(hedges: MultiHedge[]): ProviderMultiHedge[] {
    return hedges.map(hedge => {
      return {
        productType: hedge.productType,
        cross: this.cross,
        position: hedge.offerDirection,
        bidPosition: hedge.bidDirection,
        notional: cleanFormattedNumeric(hedge.amount),
        strike: +hedge.rate,
        faceCurrency: this.tabItem.rfqDetails.medusaRequest.quotePair.base,
        settlementDate: hedge.settlementDate,
        impliedSpot: +hedge.impliedSpot
      };
    });
  }

  private handleRfqDetails(): void {
    const rfqDetails = this.tabItem.rfqDetails;
    if (
      !this.tabItem.isVolBased ||
      !this.handleRfqDetailsInit ||
      !rfqDetails ||
      Object.keys(rfqDetails).length === 0
    ) {
      return;
    }
    this.rfqComment = rfqDetails.comment;
    if (rfqDetails.hydra?.setup?.hedgeMode) {
      this.hedge = rfqDetails.hydra.setup.hedgeMode;
    }

    if (rfqDetails.hydra?.hedges) {
      this.createHedges(
        this.rfqHedgeToProviderHedge(this.tabItem.rfqDetails.hydra.hedges)
      );
    }
    this.handleRfqDetailsInit = false;
  }
}
